import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import { Button, Grid, Stack, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import React, { useEffect, useMemo, useState } from "react";
import DeleteDialog from "../../../components/admin/common/CustomDeleteDialog";
import { showErrorNotification, showSuccessNotification, showWarningNotification } from "../../../components/admin/common/NotiService";
import OZDataGrid from "../../../components/admin/common/OZDataGrid/OZDataGrid";
import OZGridToolbar from "../../../components/admin/common/OZDataGrid/OZGridToolbar";
import LoadingScreen from "../../../components/admin/common/OZLoadingScreen/OZLoadingScreen";
import { deleteDIDAdditionById, getDIDAdditionList, getDIDAdditionListById } from "../../../services/api-service";
import { NewAdminPages, newAuthPages, newAuthSubAdminAccess, writeAccess } from "../../../services/page-service";
import "./DIDAddition.css";
import DIDAdditionForm from "./DIDAdditionForm";

const getResponseStatus = (response) => response?.status || response?.Status;
const getResponseMessage = (response) => response?.message || response?.Message;
const getResponseData = (response) => response?.data || response?.Data;
const isSuccessResponse = (response) => getResponseStatus(response) === "Success";
const normalizeDidData = (didData) => {
    if (!didData) return didData;
    return {
        ...didData,
        pRing: typeof didData?.pRing === "boolean" ? didData.pRing : Boolean(didData?.pring),
    };
};

function DIDAddition() {

    const ActionRenderer = (props) => {
        const [openDeleteConfirmationDialog, setDeleteConfirmationDialog] = useState(false);
        const [anchorEl, setAnchorEl] = useState(null);
        const anchorRef = React.useRef();

        const onClickEdit = () => {
            setAnchorEl(null);
            openEditForm(props.data);
        };

        const onClickDelete = () => {
            setAnchorEl(null);
            setDeleteConfirmationDialog(true);
        };

        const onMorevertIconClick = () => {
            setTimeout(() => setAnchorEl(anchorRef?.current), 1);
        };

        const onMenuClose = () => {
            setAnchorEl(null);
        };

        const onClickDeleteDialogClose = () => {
            setDeleteConfirmationDialog(false);
        };

        const onClickDeleteDialogDelete = () => {
            deleteDID(props.data.id);
            setDeleteConfirmationDialog(false);
        };

        const styleListItemButton = useMemo(
            () => ({
                "&.MuiButtonBase-root": {
                    color: "#212121",
                    fontFamily: "Roboto",
                    fontSize: "12px",
                    fontWeight: 400,
                    height: "16px",
                    lineHeight: "14px",
                },
                "&:hover": {
                    borderRadius: "4px",
                },
            }),
            []
        );

        return (
            <div className="oz-did-action-container">
                <Typography
                    sx={{
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#212121",
                        lineHeight: "16.41px",
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {props.value}
                </Typography>
                {writeAccess() &&
                    (newAuthPages(NewAdminPages.DID_ADDITION) ||
                        newAuthSubAdminAccess(NewAdminPages.DID_ADDITION, "Full")) && (
                        <img
                            className="didMorevertIcon"
                            onClick={onMorevertIconClick}
                            src={`${process.env.PUBLIC_URL}/icons/more_vert.svg`}
                            alt="edit"
                        />
                    )}
                <div ref={anchorRef}>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={onMenuClose}
                        sx={{
                            "& .MuiPaper-root": {
                                backgroundColor: "#FFFFFF",
                                width: "133px",
                                height: "62px",
                            },
                            "& .MuiMenu-paper": {
                                backgroundColor: "#FFFFFF",
                                boxShadow: "0rem 0.25rem 0.75rem rgba(0, 0, 0, 0.04)",
                                border: "0.0625rem solid #E6E5E6",
                                borderRadius: "12px",
                                marginLeft: "-0.3625rem",
                                marginTop: "0rem",
                            },
                        }}
                    >
                        <MenuItem onClick={onClickEdit} sx={{ ...styleListItemButton, marginTop: "1px" }}>
                            Edit
                        </MenuItem>
                        <MenuItem
                            onClick={onClickDelete}
                            sx={{ ...styleListItemButton, marginTop: "9px" }}
                            disabled={
                                !(
                                    newAuthPages(NewAdminPages.DID_ADDITION) ||
                                    newAuthSubAdminAccess(NewAdminPages.DID_ADDITION, "Full")
                                )
                            }
                        >
                            Delete
                        </MenuItem>
                    </Menu>
                    {openDeleteConfirmationDialog && (
                        <DeleteDialog
                            title={"Delete DID"}
                            content={
                                " This will delete this DID " +
                                (props.data.number || "") +
                                " permanently. You cannot undo this action."
                            }
                            onClose={onClickDeleteDialogClose}
                            open={true}
                            deleteFunction={onClickDeleteDialogDelete}
                        />
                    )}
                </div>
            </div>
        );
    };

    const [didForm, setDidForm] = useState({ isNew: false, show: false });
    const [didList, setDidList] = useState([]);
    const [onRowClickData, setOnRowClickData] = useState(null);
    const [gridRef, setGridRef] = useState(null);
    const [loading, setLoading] = useState(false);

    const showDidForm = () => {
        setDidForm({ isNew: true, show: true });
    };

    const closeDidForm = () => {
        setDidForm({ isNew: false, show: false });
    };

    const buttonStyle = useMemo(
        () => ({
            borderRadius: "8px",
            height: "30px",
            fontSize: "14px",
            fontWeight: 400,
            width: "149px",
            backgroundcolor: "#3D8BF8",
            lineHeight: "16.41px",
        }),
        []
    );

    useEffect(() => {
        getDIDList();
    }, []);

    async function getDIDList() {
        setLoading(true);
        try {
            getDIDAdditionList()
                .then((resp) => {
                    if (isSuccessResponse(resp)) {
                        let responseData = getResponseData(resp) || [];
                        responseData = responseData.filter((item) => item?.active === true);
                        setDidList(responseData?.map((item) => normalizeDidData(item)));
                    } else {
                        setDidList([]);
                    }
                    setLoading(false);
                })
                .catch((e) => {
                    setDidList([]);
                    setLoading(false);
                });
        } catch (e) {
            setLoading(false);
            showErrorNotification("Error in DID Addition list");
        }
    }

    async function deleteDID(dataId) {
        try {
            deleteDIDAdditionById(dataId)
                .then((resp) => {
                    if (isSuccessResponse(resp)) {
                        getDIDList();
                        showSuccessNotification(getResponseMessage(resp) || "DID deleted successfully");
                    } else {
                        showWarningNotification(getResponseMessage(resp) || "Failed to delete DID");
                    }
                })
                .catch((e) => {
                    showErrorNotification("Error deleting DID");
                });
        } catch (e) {
            showErrorNotification("Error in DID delete");
        }
    }

    const columnDefs = [
        { field: "number", headerName: "Number" },
        { field: "priDetails.priName", headerName: "PRI" },
        {
            field: "priOwner",
            headerName: "PRI Owner",
            valueGetter: ({ data }) => data?.priOwner || data?.priDetails?.priOwner || "-",
        },
        { field: "callerId", headerName: "Caller ID" },
        { field: "appUrl", headerName: "App Url", cellRenderer: ActionRenderer },
    ];

    async function openEditForm(rowData) {
        if (!rowData?.id) {
            showErrorNotification("Error fetching DID details");
            return;
        }

        const fallbackDidData = normalizeDidData(rowData);
        setOnRowClickData(fallbackDidData);
        setDidForm({ isNew: false, show: true });

        try {
            const resp = await getDIDAdditionListById(rowData.id);
            const responseData = getResponseData(resp);
            const didData = Array.isArray(responseData) ? responseData[0] : responseData;
            if (isSuccessResponse(resp) && didData) {
                setOnRowClickData(normalizeDidData(didData));
            } else {
                showErrorNotification(getResponseMessage(resp) || "Error fetching DID details");
            }
        } catch (e) {
            showErrorNotification("Error fetching DID details");
        }
    }

    async function onClickRow(rowItem) {
        openEditForm(rowItem.data);
    }

    return (
        <div className="ozAdminDIDAdditionContainer">
            <Grid sx={{ marginBottom: "10px" }}>
                <Typography className="oz-page-header"> Configurations </Typography>
            </Grid>
            <Stack direction="row" sx={{ marginBottom: "16px" }} justifyContent="space-between">
                <Typography className="oz-page-heading"> DID Addition </Typography>
                {writeAccess() &&
                    (newAuthPages(NewAdminPages.DID_ADDITION) ||
                        newAuthSubAdminAccess(NewAdminPages.DID_ADDITION, "Full")) && (
                        <Button
                            sx={buttonStyle}
                            variant="contained"
                            startIcon={<AddBoxOutlinedIcon />}
                            onClick={showDidForm}
                        >
                            Add DID
                        </Button>
                    )}
            </Stack>
            {didForm.show && didForm.isNew && (
                <DIDAdditionForm refreshList={getDIDList} onClose={closeDidForm} />
            )}
            {didForm.show && !didForm.isNew && (
                <DIDAdditionForm
                    refreshList={getDIDList}
                    onClose={closeDidForm}
                    data={onRowClickData}
                    editAccess={
                        writeAccess() &&
                        (newAuthPages(NewAdminPages.DID_ADDITION) ||
                            newAuthSubAdminAccess(NewAdminPages.DID_ADDITION, "Full") ||
                            newAuthSubAdminAccess(NewAdminPages.DID_ADDITION, "Edit"))
                    }
                />
            )}
            <OZGridToolbar
                title="List of DID"
                searchPlaceholder="Number"
                rowCount={didList.length}
                dataGridRef={gridRef}
                fileName="DIDAddition"
                refreshGrid={getDIDList}
            />
            {loading ? (
                <div
                    style={{
                        position: "fixed",
                        top: "0",
                        left: "0",
                        width: "100%",
                        height: "100%",
                        backgroundColor: "0,0,0,0.5",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                >
                    <LoadingScreen />
                </div>
            ) : (
                <OZDataGrid
                    rowClickCallback={onClickRow}
                    height="26.3rem"
                    data={didList}
                    columns={columnDefs}
                    setGridRef={setGridRef}
                />
            )}
        </div>
    );
}

export default DIDAddition;
